(() => {
  const rootUrl = () => window.Shopify?.routes?.root || '/';

  const money = (cents) => {
    if (cents === null || cents === undefined || Number.isNaN(Number(cents))) return 'Pris kommer snart';
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'SEK',
      maximumFractionDigits: 2
    }).format(Number(cents) / 100);
  };

  const cleanUrl = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname;
    } catch (_) {
      return url || '';
    }
  };

  const productImage = (product) => {
    const image = product?.image || product?.featured_image?.url || product?.featured_image;
    if (!image) return '';
    return typeof image === 'string' ? image : image.url || '';
  };

  async function predictiveSearch(query, limit = 10) {
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': String(limit),
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'show',
      'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag'
    });
    const response = await fetch(`${rootUrl()}search/suggest.json?${params}`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Search failed: ${response.status}`);
    const data = await response.json();
    return data?.resources?.results?.products || [];
  }

  function createProductResultCard(product, index, reason, matchLabel) {
    const article = document.createElement('article');
    article.className = 'npc-match-card';
    const productSignature = `${product.vendor || ''} ${product.title || ''}`.toLowerCase();
    if (productSignature.includes('dyrisk')) {
      article.classList.add('npc-match-card--compact-media');
    }

    const rank = document.createElement('span');
    rank.className = 'npc-match-card__rank';
    rank.textContent = `0${index + 1}`;

    const media = document.createElement('a');
    media.className = 'npc-match-card__media';
    media.href = cleanUrl(product.url);
    const imageUrl = productImage(product);
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = product.title || '';
      image.loading = 'lazy';
      media.appendChild(image);
    }

    const copy = document.createElement('div');
    copy.className = 'npc-match-card__copy';
    const vendor = document.createElement('small');
    vendor.textContent = product.vendor || 'Nordic Pet House';
    const title = document.createElement('h3');
    title.textContent = product.title || 'Produkt';
    const score = document.createElement('span');
    score.className = 'npc-match-card__score';
    score.textContent = matchLabel || 'Utifrån produktuppgifter';
    const why = document.createElement('p');
    why.textContent = reason;
    const meta = document.createElement('div');
    meta.className = 'npc-match-card__meta';
    const type = document.createElement('span');
    type.textContent = product.type || 'Läs produktinformationen';
    const price = document.createElement('strong');
    price.textContent = product.price || 'Pris kommer snart';
    meta.append(type, price);
    const actions = document.createElement('div');
    actions.className = 'npc-match-card__actions';
    const link = document.createElement('a');
    link.className = 'npc-btn';
    link.href = cleanUrl(product.url);
    link.textContent = 'Se produkten';
    const compare = document.createElement('button');
    compare.type = 'button';
    compare.className = 'npc-text-link';
    compare.dataset.npcCompareProduct = cleanUrl(product.url).split('/').filter(Boolean).pop() || '';
    compare.textContent = 'Jämför +';
    actions.append(link, compare);
    copy.append(vendor, title, score, why, meta, actions);

    article.append(rank, media, copy);
    return article;
  }

  class NpcMatch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-npc-match-form]');
      if (!this.form) return;
      this.steps = [...this.querySelectorAll('[data-npc-match-step]')];
      this.next = this.querySelector('[data-npc-match-next]');
      this.back = this.querySelector('[data-npc-match-back]');
      this.submit = this.querySelector('[data-npc-match-submit]');
      this.results = this.querySelector('[data-npc-match-results]');
      this.grid = this.querySelector('[data-npc-match-grid]');
      this.loading = this.querySelector('[data-npc-match-loading]');
      this.empty = this.querySelector('[data-npc-match-empty]');
      this.summary = this.querySelector('[data-npc-match-summary]');
      this.progress = this.querySelector('[data-npc-match-progress]');
      this.progressLabel = this.querySelector('[data-npc-match-progress-label]');
      this.matchActions = this.querySelector('[data-npc23-match-actions]');
      this.saveButton = this.querySelector('[data-npc23-save-match]');
      this.emailLink = this.querySelector('[data-npc23-email-match]');
      this.saveStatus = this.querySelector('[data-npc-match-save-status]');
      this.profilePanel = this.querySelector('[data-npc-match-profiles]');
      this.profileList = this.querySelector('[data-npc-match-profile-list]');
      this.petNameInput = this.querySelector('[data-npc-match-pet-name]');
      this.youngLabel = this.querySelector('[data-npc-match-young-label]');
      this.youngNote = this.querySelector('[data-npc-match-young-note]');
      this.current = 0;
      this.requestId = 0;

      this.next.addEventListener('click', () => this.move(1));
      this.back.addEventListener('click', () => this.move(-1));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.querySelector('[data-npc-match-restart]')?.addEventListener('click', () => this.restart());
      this.saveButton?.addEventListener('click', () => this.saveMatch());
      this.steps.forEach((step) => {
        step.addEventListener('change', () => {
          step.classList.remove('has-error');
        });
      });
      this.restorePetProfiles();
      this.update();
    }

    restorePetProfiles() {
      if (!this.profilePanel || !this.profileList) return;
      const customerKey = String(this.dataset.customerKey || '').trim();
      if (!customerKey) return;
      let state = null;
      try {
        state = JSON.parse(localStorage.getItem(`npc:pet-profiles:${customerKey}`) || 'null');
        if (!state) {
          const legacy = JSON.parse(localStorage.getItem(`npc:pet-profile:${customerKey}`) || 'null');
          if (legacy?.name) state = { profiles: [{ ...legacy, id: `pet-${Date.now()}` }] };
        }
      } catch (_) {}
      const profiles = Array.isArray(state?.profiles) ? state.profiles : [];
      if (!profiles.length) return;

      this.profilePanel.hidden = false;
      this.profileList.replaceChildren();
      profiles.slice(0, 6).forEach((profile) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.npcMatchProfile = profile.id || '';
        const name = document.createElement('strong');
        name.textContent = profile.name || 'Djurprofil';
        const details = document.createElement('span');
        details.textContent = `${profile.animal === 'cat' ? 'Katt' : 'Hund'}${profile.weight ? ` · ${profile.weight} kg` : ''}`;
        button.append(name, details);
        button.addEventListener('click', () => this.applyPetProfile(profile, button));
        this.profileList.appendChild(button);
      });
    }

    applyPetProfile(profile, button) {
      this.profileList?.querySelectorAll('button').forEach((item) => item.classList.toggle('is-active', item === button));
      const animal = this.form.querySelector(`input[name="animal"][value="${profile.animal === 'cat' ? 'cat' : 'dog'}"]`);
      if (animal) animal.checked = true;

      let stage = 'adult';
      if (profile.birthday) {
        const birthday = new Date(`${profile.birthday}T12:00:00`);
        const ageMonths = Number.isNaN(birthday.getTime()) ? 0 : ((Date.now() - birthday.getTime()) / 2629800000);
        if (ageMonths > 0 && ageMonths < 14) stage = 'young';
        if (ageMonths >= 96) stage = 'senior';
      }
      const stageInput = this.form.querySelector(`input[name="stage"][value="${stage}"]`);
      if (stageInput) stageInput.checked = true;

      const weight = Number(String(profile.weight || '').replace(',', '.'));
      const size = weight > 0 && weight < 10 ? 'small' : weight > 25 ? 'large' : 'medium';
      const sizeInput = this.form.querySelector(`input[name="size"][value="${size}"]`);
      if (sizeInput) sizeInput.checked = true;
      if (this.petNameInput) this.petNameInput.value = profile.name || '';

      this.current = Math.min(3, this.steps.length - 1);
      this.update();
      window.NPCTrack?.('npc_match_profile_selected', {
        animal: profile.animal === 'cat' ? 'cat' : 'dog',
        has_birthday: Boolean(profile.birthday),
        has_weight: Boolean(profile.weight)
      });
      this.steps[this.current]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    move(direction) {
      if (direction > 0 && !this.validateCurrent()) return;
      this.current = Math.max(0, Math.min(this.steps.length - 1, this.current + direction));
      this.update();
      this.steps[this.current]?.querySelector('input:checked, input')?.focus({ preventScroll: true });
    }

    validateCurrent() {
      const checked = this.steps[this.current].querySelector('input:checked');
      this.steps[this.current].classList.toggle('has-error', !checked);
      if (!checked) {
        this.steps[this.current].querySelector('input')?.focus();
        return false;
      }
      return true;
    }

    update() {
      const selectedAnimal = this.form.elements.animal?.value || '';
      if (this.youngLabel) {
        this.youngLabel.textContent = selectedAnimal === 'cat'
          ? 'Kattunge'
          : selectedAnimal === 'dog' ? 'Valp' : 'Valp / kattunge';
      }
      if (this.youngNote) {
        this.youngNote.textContent = selectedAnimal === 'cat'
          ? 'Kattens växande livsfas'
          : selectedAnimal === 'dog' ? 'Hundens växande livsfas' : 'Växande hund eller katt';
      }

      this.steps.forEach((step, index) => {
        const active = index === this.current;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
      });
      const last = this.current === this.steps.length - 1;
      this.back.hidden = this.current === 0;
      this.next.hidden = last;
      this.submit.hidden = !last;
      const percent = ((this.current + 1) / this.steps.length) * 100;
      this.progress.style.setProperty('--npc-progress', `${percent}%`);
      this.progressLabel.textContent = `Steg ${this.current + 1} av ${this.steps.length}`;
    }

    values() {
      return Object.fromEntries(new FormData(this.form).entries());
    }


    summaryText(values) {
      const animal = values.animal === 'cat' ? 'katt' : 'hund';
      const size = values.size === 'small' ? 'liten' : values.size === 'large' ? 'stor' : 'mellanstor';
      const subject = values.stage === 'young'
        ? `${size} ${values.animal === 'cat' ? 'kattunge' : 'valp'}`
        : `${size} ${values.stage === 'senior' ? 'äldre' : 'vuxen'} ${animal}`;
      const format = values.format === 'wet' ? 'våtfoder' : values.format === 'dry' ? 'torrfoder' : 'torr- eller våtfoder';
      const protein = values.protein === 'fish'
        ? 'fisk'
        : values.protein === 'poultry'
          ? 'fågel'
          : values.protein === 'meat' ? 'kött' : 'valfri proteinkälla';
      const owner = values.pet_name ? `För ${values.pet_name} söker du` : 'Du söker';
      return `${owner} ${format} för en ${subject}, med ${protein}. Vi filtrerar på djurslag, angiven livsfas och foderformat. Proteinönskemål och särskilda behov kan bara bedömas när uppgifterna finns. Läs förklaringen vid varje produkt.`;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.validateCurrent()) return;
      const values = this.values();
      const requestId = ++this.requestId;
      window.NPCTrack?.('npc_match_complete', {
        animal: values.animal,
        stage: values.stage,
        format: values.format,
        protein: values.protein
      });
      this.form.hidden = true;
      this.results.hidden = false;
      this.grid.replaceChildren();
      this.empty.hidden = true;
      this.loading.hidden = false;
      if (this.matchActions) this.matchActions.hidden = true;
      if (this.saveStatus) this.saveStatus.textContent = '';
      this.summary.textContent = this.summaryText(values);
      this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.querySelector('[data-npc-match-title]')?.focus({ preventScroll: true });

      try {
        const products = await window.NPCProductCore.catalog();
        if (requestId !== this.requestId || !this.isConnected) return;
        const ranked = products
          .map((product) => window.NPCProductCore.assess(product, values))
          .filter(Boolean)
          .sort((a, b) => b.score - a.score || String(a.product.title).localeCompare(String(b.product.title), 'sv'))
          .slice(0, 3);

        this.loading.hidden = true;
        if (!ranked.length) {
          const fallback = this.querySelector('[data-npc-match-fallback]');
          fallback.href = values.animal === 'cat' ? '/collections/katt' : '/collections/hund';
          this.empty.hidden = false;
          return;
        }

        const storedProducts = [];
        ranked.forEach(({ product, reason, label }, index) => {
          this.grid.appendChild(createProductResultCard(product, index, reason, label));
          storedProducts.push({
            handle: cleanUrl(product.url).split('/').filter(Boolean).pop() || '',
            title: product.title || 'Produkt',
            vendor: product.vendor || '',
            url: cleanUrl(product.url),
            image: productImage(product),
            reason,
            matchLabel: label
          });
        });
        this.lastMatch = {
          createdAt: new Date().toISOString(),
          summary: this.summaryText(values),
          values,
          products: storedProducts
        };
        if (this.matchActions) this.matchActions.hidden = false;
        if (this.emailLink) {
          const productLines = storedProducts.map((product, index) => `${index + 1}. ${product.title} — ${product.reason} — ${window.location.origin}${product.url}`).join('\n');
          const body = `Min Nordic Match\n\n${this.lastMatch.summary}\n\n${productLines}\n\nResultatet är vägledande och ersätter inte veterinärrådgivning.`;
          this.emailLink.href = `mailto:?subject=${encodeURIComponent('Min Nordic Match')}&body=${encodeURIComponent(body)}`;
        }
      } catch (_) {
        if (requestId !== this.requestId || !this.isConnected) return;
        this.loading.hidden = true;
        const fallback = this.querySelector('[data-npc-match-fallback]');
        fallback.href = values.animal === 'cat' ? '/collections/katt' : '/collections/hund';
        this.empty.hidden = false;
      }
    }

    saveMatch() {
      if (!this.lastMatch) return;
      try {
        localStorage.setItem('npc:last-match', JSON.stringify(this.lastMatch));
        window.NPCTrack?.('npc_match_saved', {
          animal: this.lastMatch.values?.animal,
          format: this.lastMatch.values?.format
        });
        if (this.saveStatus) this.saveStatus.textContent = 'Matchningen är sparad på den här enheten och visas i din medlemshubb.';
      } catch (_) {
        if (this.saveStatus) this.saveStatus.textContent = 'Matchningen kunde inte sparas i den här webbläsaren.';
      }
    }

    restart() {
      this.requestId += 1;
      this.lastMatch = null;
      this.form.reset();
      this.current = 0;
      this.form.hidden = false;
      this.results.hidden = true;
      this.grid.replaceChildren();
      if (this.matchActions) this.matchActions.hidden = true;
      this.lastMatch = null;
      this.update();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function extractMetric(text, patterns) {
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) return `${match[1].replace('.', ',')} %`;
    }
    return 'Ej angivet';
  }

  function extractEnergy(text) {
    const match = text.match(/(\d{3,5})\s*kcal\s*\/?\s*kg/i);
    return match ? `${Number(match[1]).toLocaleString('sv-SE')} kcal/kg` : 'Ej angivet';
  }


  function variantWeightGrams(variant, productTitle, allowProductTitleFallback = true) {
    return window.NPCProductCore.netGrams(variant, productTitle, allowProductTitleFallback);
  }

  function formatWeight(grams) {
    if (!(grams > 0)) return 'Okänd storlek';
    if (grams >= 1000) {
      return `${(grams / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 2 })} kg`;
    }
    return `${Math.round(grams).toLocaleString('sv-SE')} g`;
  }

  class NpcCompare extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-npc-compare-input]');
      this.suggestions = this.querySelector('[data-npc-compare-suggestions]');
      this.status = this.querySelector('[data-npc-compare-status]');
      this.grid = this.querySelector('[data-npc-compare-grid]');
      this.empty = this.querySelector('[data-npc-compare-empty]');
      this.clear = this.querySelector('[data-npc-compare-clear]');
      this.summary = this.querySelector('[data-npc23-compare-summary]');
      this.share = this.querySelector('[data-npc23-compare-share]');
      this.products = [];
      this.timer = null;

      this.input?.addEventListener('input', () => this.queueSearch());
      this.suggestions?.addEventListener('click', (event) => this.onSuggestion(event));
      this.grid?.addEventListener('click', (event) => this.onGridClick(event));
      this.grid?.addEventListener('change', (event) => this.onVariantChange(event));
      this.grid?.addEventListener('input', (event) => this.onDailyInput(event));
      this.clear?.addEventListener('click', () => this.reset());
      this.share?.addEventListener('click', () => this.copyShareLink());
      this.querySelector('[data-npc-compare-starters]')?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-npc-compare-starter]');
        if (button) this.addProduct(button.dataset.npcCompareStarter);
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.closeSuggestions();
      });
      this.loadInitialProducts();
    }

    queueSearch() {
      window.clearTimeout(this.timer);
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.closeSuggestions();
        this.status.textContent = 'Skriv minst två tecken.';
        return;
      }
      this.status.textContent = 'Söker i sortimentet…';
      this.timer = window.setTimeout(() => this.search(query), 220);
    }

    async search(query) {
      try {
        const products = await predictiveSearch(query, 10);
        this.renderSuggestions(products);
      } catch (_) {
        this.status.textContent = 'Sökningen kunde inte laddas. Försök igen.';
      }
    }

    renderSuggestions(products) {
      this.suggestions.replaceChildren();
      if (!products.length) {
        const empty = document.createElement('p');
        empty.textContent = 'Ingen produkt hittades. Prova ett kortare sökord.';
        this.suggestions.appendChild(empty);
      } else {
        products.forEach((product, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.compareSuggestion = String(index);
          const imageUrl = productImage(product);
          if (imageUrl) {
            const image = document.createElement('img');
            image.src = imageUrl;
            image.alt = '';
            image.loading = 'lazy';
            button.appendChild(image);
          }
          const copy = document.createElement('span');
          const title = document.createElement('strong');
          title.textContent = product.title;
          const meta = document.createElement('small');
          meta.textContent = [product.vendor, product.type].filter(Boolean).join(' · ');
          copy.append(title, meta);
          const action = document.createElement('b');
          action.textContent = this.products.some((item) => item.handle === this.handleFromUrl(product.url)) ? 'Vald' : 'Välj +';
          button.append(copy, action);
          this.suggestions.appendChild(button);
        });
      }
      this.currentSuggestions = products;
      this.suggestions.hidden = false;
      this.status.textContent = products.length ? `${products.length} produkter hittades.` : 'Inga träffar.';
    }

    onSuggestion(event) {
      const button = event.target.closest('[data-compare-suggestion]');
      if (!button) return;
      const product = this.currentSuggestions?.[Number(button.dataset.compareSuggestion)];
      if (product) this.addProduct(this.handleFromUrl(product.url));
    }

    handleFromUrl(url) {
      return cleanUrl(url).split('/').filter(Boolean).pop() || '';
    }

    async fetchProduct(handle) {
      const productUrl = `${rootUrl()}products/${encodeURIComponent(handle)}`;
      const [jsonResponse, htmlResponse] = await Promise.all([
        fetch(`${productUrl}.js`, { headers: { Accept: 'application/json' } }),
        fetch(productUrl, { headers: { Accept: 'text/html' } })
      ]);
      if (!jsonResponse.ok) throw new Error('Product JSON unavailable');
      const data = await jsonResponse.json();
      let pageText = '';
      if (htmlResponse.ok) {
        const html = await htmlResponse.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const facts = doc.querySelector('.npc-product-faq, .faq, [data-section-type="faq"]');
        pageText = (facts || doc.body)?.textContent || '';
      }
      return {
        handle,
        id: data.id,
        title: data.title,
        vendor: data.vendor,
        type: data.type || data.product_type || 'Produkt',
        url: productUrl,
        image: data.featured_image || data.images?.[0] || '',
        variants: data.variants || [],
        tags: data.tags || [],
        protein: extractMetric(pageText, [/(?:råprotein|protein)\s*:?\s*(\d+(?:[.,]\d+)?)\s*%/i]),
        fat: extractMetric(pageText, [/(?:fettinnehåll|råfett|fett)\s*:?\s*(\d+(?:[.,]\d+)?)\s*%/i]),
        moisture: extractMetric(pageText, [/(?:vattenhalt|vatten|fukthalt)\s*:?\s*(\d+(?:[.,]\d+)?)\s*%/i]),
        energy: extractEnergy(pageText)
      };
    }

    async addProduct(handle) {
      if (!handle || this.products.some((product) => product.handle === handle)) {
        this.closeSuggestions();
        this.input.value = '';
        this.status.textContent = 'Produkten är redan vald.';
        this.syncStarterState();
        return;
      }
      if (this.products.length >= 3) {
        this.status.textContent = 'Du kan jämföra högst tre produkter. Ta bort en för att välja en annan.';
        return;
      }
      this.status.textContent = 'Hämtar produktinformationen…';
      try {
        const product = await this.fetchProduct(handle);
        this.products.push(product);
        this.persist();
        this.render();
        this.input.value = '';
        this.closeSuggestions();
        this.status.textContent = this.products.length < 2
          ? 'Välj minst en produkt till.'
          : `${this.products.length} produkter redo att jämföras.`;
      } catch (_) {
        this.status.textContent = 'Produkten kunde inte hämtas. Försök med en annan.';
      }
    }

    selectedVariant(product, variantId) {
      return product.variants.find((variant) => String(variant.id) === String(variantId)) || product.variants[0] || {};
    }

    render() {
      this.grid.replaceChildren();
      this.empty.hidden = this.products.length > 0;
      this.clear.hidden = this.products.length === 0;
      this.products.forEach((product, index) => this.grid.appendChild(this.createCompareCard(product, index)));
      this.renderSummary();
      if (this.share) this.share.hidden = this.products.length === 0;
      this.syncStarterState();
    }

    syncStarterState() {
      this.querySelectorAll('[data-npc-compare-starter]').forEach((button) => {
        const selected = this.products.some((product) => product.handle === button.dataset.npcCompareStarter);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
        const label = button.querySelector('[data-npc-compare-starter-label]');
        if (label) label.textContent = selected ? 'Vald ✓' : 'Välj +';
      });
    }

    createCompareCard(product, index) {
      const article = document.createElement('article');
      article.className = 'npc-compare-card';
      article.dataset.compareHandle = product.handle;
      const productSignature = `${product.vendor || ''} ${product.title || ''}`.toLowerCase();
      if (productSignature.includes('dyrisk')) {
        article.classList.add('npc-compare-card--compact-media');
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'npc-compare-card__remove';
      remove.dataset.removeCompare = product.handle;
      remove.setAttribute('aria-label', `Ta bort ${product.title}`);
      remove.textContent = '×';

      const rank = document.createElement('span');
      rank.className = 'npc-compare-card__rank';
      rank.textContent = `0${index + 1}`;

      const image = document.createElement('img');
      image.className = 'npc-compare-card__image';
      image.src = product.image;
      image.alt = product.title;
      image.loading = 'lazy';

      const vendor = document.createElement('small');
      vendor.className = 'npc-compare-card__vendor';
      vendor.textContent = product.vendor || 'Nordic Pet House';
      const title = document.createElement('h3');
      title.textContent = product.title;

      const variant = product.variants[0] || {};
      let variantControl;
      if (product.variants.length > 1) {
        variantControl = document.createElement('select');
        variantControl.dataset.compareVariant = product.handle;
        variantControl.setAttribute('aria-label', `Välj variant för ${product.title}`);
        product.variants.forEach((item) => {
          const option = document.createElement('option');
          option.value = item.id;
          option.textContent = item.title === 'Default Title' ? 'Standard' : item.title;
          variantControl.appendChild(option);
        });
      } else {
        variantControl = document.createElement('p');
        variantControl.className = 'npc-compare-card__variant';
        variantControl.textContent = variant.title && variant.title !== 'Default Title' ? variant.title : 'Standardförpackning';
      }

      const price = document.createElement('strong');
      price.className = 'npc-compare-card__price';
      price.dataset.comparePrice = product.handle;
      price.textContent = Number(variant.price) > 500 ? money(variant.price) : 'Pris kommer snart';

      const unit = document.createElement('span');
      unit.className = 'npc-compare-card__unit';
      unit.dataset.compareUnit = product.handle;
      unit.textContent = this.unitPrice(variant, product.title);

      const daily = document.createElement('div');
      daily.className = 'npc-compare-card__daily';
      const dailyLabel = document.createElement('label');
      dailyLabel.textContent = 'Daglig mängd i gram';
      const dailyInput = document.createElement('input');
      dailyInput.type = 'number';
      dailyInput.min = '1';
      dailyInput.step = '1';
      dailyInput.value = '200';
      dailyInput.inputMode = 'numeric';
      dailyInput.dataset.compareDaily = product.handle;
      const dailyOutput = document.createElement('output');
      dailyOutput.dataset.compareDailyOutput = product.handle;
      dailyOutput.textContent = this.dailyCost(product, variant, 200);
      dailyLabel.appendChild(dailyInput);
      daily.append(dailyLabel, dailyOutput);

      const facts = document.createElement('dl');
      [
        ['Produkttyp', product.type || 'Ej angivet'],
        ['Råprotein', product.protein],
        ['Fett', product.fat],
        ['Vatten', product.moisture],
        ['Energi', product.energy]
      ].forEach(([label, value]) => {
        const row = document.createElement('div');
        const term = document.createElement('dt');
        term.textContent = label;
        const description = document.createElement('dd');
        description.textContent = value;
        row.append(term, description);
        facts.appendChild(row);
      });

      const link = document.createElement('a');
      link.className = 'npc-btn';
      link.href = product.url;
      link.textContent = 'Läs hela produktsidan';

      article.append(remove, rank, image, vendor, title, variantControl, price, unit, daily, facts, link);
      return article;
    }

    unitPrice(variant, productTitle) {
      const grams = window.NPCProductCore.netGrams(variant, productTitle, !variant.title || variant.title.toLowerCase() === 'default title');
      if (!grams || Number(variant.price) <= 500) return grams ? `${(grams / 1000).toLocaleString('sv-SE')} kg` : 'Vikt framgår på produktsidan';
      const perHundred = Number(variant.price) / grams * 100;
      return `${money(perHundred)} / 100 g`;
    }

    dailyCost(product, variant, dailyGrams) {
      const grams = window.NPCProductCore.netGrams(variant, product.title, product.variants?.length <= 1 || variant.title?.toLowerCase() === 'default title');
      if (!grams) return 'Lägg in förpackningsvikt på produkten för kostnad per dag.';
      if (Number(variant.price) <= 500) return `${Number(dailyGrams).toLocaleString('sv-SE')} g/dag · pris kommer snart`;
      const cost = (Number(variant.price) / 100) / grams * Number(dailyGrams);
      return `Cirka ${cost.toLocaleString('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 2 })} per dag`;
    }

    renderSummary() {
      if (!this.summary) return;
      if (this.products.length < 2) {
        this.summary.hidden = true;
        this.summary.replaceChildren();
        return;
      }
      const withProtein = this.products
        .map((product) => ({ product, value: Number(String(product.protein || '').replace(',', '.').match(/\d+(?:\.\d+)?/)?.[0]) }))
        .filter((entry) => Number.isFinite(entry.value))
        .sort((a, b) => b.value - a.value);
      const heading = document.createElement('strong');
      const copy = document.createElement('p');
      heading.textContent = `${this.products.length} produkter sida vid sida.`;
      copy.textContent = withProtein.length
        ? `${withProtein[0].product.title} har högst angiven råproteinhalt i den hämtade produktinformationen (${withProtein[0].product.protein}). Kontrollera alltid den aktuella förpackningen.`
        : 'Jämför format, variant, pris och de värden som finns angivna. Tomma datapunkter betyder att informationen inte kunde läsas automatiskt från produktsidan.';
      this.summary.replaceChildren(heading, copy);
      this.summary.hidden = false;
    }

    onGridClick(event) {
      const button = event.target.closest('[data-remove-compare]');
      if (!button) return;
      this.products = this.products.filter((product) => product.handle !== button.dataset.removeCompare);
      this.persist();
      this.render();
      this.status.textContent = this.products.length < 2 ? 'Välj minst två produkter.' : `${this.products.length} produkter valda.`;
    }

    onVariantChange(event) {
      const select = event.target.closest('[data-compare-variant]');
      if (!select) return;
      const product = this.products.find((item) => item.handle === select.dataset.compareVariant);
      if (!product) return;
      const variant = this.selectedVariant(product, select.value);
      const price = this.querySelector(`[data-compare-price="${CSS.escape(product.handle)}"]`);
      const unit = this.querySelector(`[data-compare-unit="${CSS.escape(product.handle)}"]`);
      if (price) price.textContent = Number(variant.price) > 500 ? money(variant.price) : 'Pris kommer snart';
      if (unit) unit.textContent = this.unitPrice(variant, product.title);
      const dailyInput = this.querySelector(`[data-compare-daily="${CSS.escape(product.handle)}"]`);
      const dailyOutput = this.querySelector(`[data-compare-daily-output="${CSS.escape(product.handle)}"]`);
      if (dailyOutput) dailyOutput.textContent = this.dailyCost(product, variant, Number(dailyInput?.value || 200));
    }

    onDailyInput(event) {
      const input = event.target.closest('[data-compare-daily]');
      if (!input) return;
      const product = this.products.find((item) => item.handle === input.dataset.compareDaily);
      if (!product) return;
      const select = this.querySelector(`[data-compare-variant="${CSS.escape(product.handle)}"]`);
      const variant = this.selectedVariant(product, select?.value);
      const output = this.querySelector(`[data-compare-daily-output="${CSS.escape(product.handle)}"]`);
      if (output) output.textContent = this.dailyCost(product, variant, Number(input.value || 0));
    }

    persist() {
      const handles = this.products.map((product) => product.handle);
      try {
        localStorage.setItem('npc:compare-products', JSON.stringify(handles));
      } catch (_) {}
      const url = new URL(window.location.href);
      if (handles.length) url.searchParams.set('products', handles.join(','));
      else url.searchParams.delete('products');
      history.replaceState({}, '', url);
    }

    async loadInitialProducts() {
      const fromUrl = new URL(window.location.href).searchParams.get('products')?.split(',') || [];
      let stored = [];
      try {
        stored = JSON.parse(localStorage.getItem('npc:compare-products') || '[]');
      } catch (_) {}
      const handles = [...new Set([...fromUrl, ...stored])].filter(Boolean).slice(0, 3);
      for (const handle of handles) await this.addProduct(handle);
    }

    reset() {
      this.products = [];
      this.persist();
      this.render();
      this.status.textContent = 'Välj minst två och högst tre produkter.';
    }

    async copyShareLink() {
      try {
        await navigator.clipboard.writeText(window.location.href);
        this.status.textContent = 'Jämförelselänken är kopierad.';
      } catch (_) {
        this.status.textContent = 'Kopiera adressen i webbläsaren för att dela jämförelsen.';
      }
    }

    closeSuggestions() {
      if (this.suggestions) this.suggestions.hidden = true;
    }
  }

  class NpcDailyCost extends HTMLElement {
    connectedCallback() {
      this.gramsInput = this.querySelector('[data-npc-daily-grams]');
      this.packInput = this.querySelector('[data-npc-daily-pack]');
      this.manualWeight = this.querySelector('[data-npc-daily-manual]');
      this.variantOutput = this.querySelector('[data-npc-daily-variant]');
      this.costOutput = this.querySelector('[data-npc-daily-cost]');
      this.daysOutput = this.querySelector('[data-npc-daily-days]');
      this.monthOutput = this.querySelector('[data-npc-daily-month]');
      this.basisOutput = this.querySelector('[data-npc-daily-basis]');

      try {
        this.variants = JSON.parse(this.querySelector('[data-npc-daily-variants]')?.textContent || '[]');
      } catch (_) {
        this.variants = [];
      }

      this.productForm = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
      this.abortController = new AbortController();
      const listenerOptions = { signal: this.abortController.signal };

      this.gramsInput?.addEventListener('input', () => this.calculate(), listenerOptions);
      this.packInput?.addEventListener('input', () => this.calculate(), listenerOptions);
      this.productForm?.addEventListener('variant:change', (event) => this.syncVariant(event.detail?.variant), listenerOptions);
      this.productForm?.addEventListener('change', (event) => {
        if (event.target?.name === 'id') window.setTimeout(() => this.syncVariant(), 0);
      }, listenerOptions);

      this.syncVariant();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    syncVariant(providedVariant) {
      const idInput = this.productForm?.querySelector('[name="id"]');
      const variant = providedVariant
        || this.variants.find((item) => String(item.id) === String(idInput?.value))
        || this.variants[0]
        || {};

      this.variant = variant;
      this.price = Number(variant.price || 0);
      const variantTitle = String(variant.title || '').trim();
      const allowProductTitleFallback = this.variants.length <= 1 || variantTitle.toLowerCase() === 'default title';
      this.detectedWeight = variantWeightGrams(variant, this.dataset.productTitle, allowProductTitleFallback);

      if (this.manualWeight) this.manualWeight.hidden = this.detectedWeight > 0;
      if (this.detectedWeight > 0 && this.packInput) this.packInput.value = '';

      const weightLabel = formatWeight(this.detectedWeight);
      if (this.variantOutput) {
        this.variantOutput.textContent = variantTitle && variantTitle.toLowerCase() !== 'default title'
          ? variantTitle
          : weightLabel;
      }

      this.calculate();
    }

    calculate() {
      const gramsPerDay = Number(String(this.gramsInput?.value || '').replace(',', '.'));
      const manualPack = Number(String(this.packInput?.value || '').replace(',', '.'));
      const packGrams = this.detectedWeight > 0 ? this.detectedWeight : manualPack;
      const price = this.price;

      if (!(gramsPerDay > 0)) {
        this.setResults('—', '—', '—');
        if (this.basisOutput) {
          this.basisOutput.textContent = packGrams > 0 && price > 500
            ? `Vald förpackning: ${formatWeight(packGrams)} för ${money(price)}.`
            : 'Välj storlek och ange gram per dag.';
        }
        return;
      }

      if (!(packGrams > 0)) {
        this.setResults('Ange vikt', '—', '—');
        if (this.basisOutput) this.basisOutput.textContent = 'Ange förpackningens vikt för att kunna räkna.';
        return;
      }

      const days = packGrams / gramsPerDay;
      const daysLabel = `ca ${days.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} dagar`;

      if (!(price > 500)) {
        this.setResults('Pris saknas', daysLabel, '—');
        if (this.basisOutput) this.basisOutput.textContent = `Beräknat på ${formatWeight(packGrams)} och ${gramsPerDay.toLocaleString('sv-SE')} g per dag.`;
        return;
      }

      const dailyCostCents = price * gramsPerDay / packGrams;
      this.setResults(money(dailyCostCents), daysLabel, money(dailyCostCents * 30));
      if (this.basisOutput) {
        this.basisOutput.textContent = `Beräknat på ${formatWeight(packGrams)} för ${money(price)} och ${gramsPerDay.toLocaleString('sv-SE')} g per dag.`;
      }
    }

    setResults(cost, days, month) {
      if (this.costOutput) this.costOutput.textContent = cost;
      if (this.daysOutput) this.daysOutput.textContent = days;
      if (this.monthOutput) this.monthOutput.textContent = month;
    }
  }

  class NpcPetProfile extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.storageKey = this.dataset.storageKey || 'npc:pet-profiles';
      this.tagsInput = this.querySelector('[data-npc-pet-tags]');
      this.idInput = this.querySelector('[data-npc-pet-id]');
      this.list = this.querySelector('[data-npc-pet-list]');
      this.count = this.querySelector('[data-npc-pet-count]');
      this.status = this.querySelector('[data-npc-pet-status]');
      this.deleteButton = this.querySelector('[data-npc-pet-delete]');
      this.mode = this.querySelector('[data-npc-pet-form-mode]');
      this.formTitle = this.querySelector('[data-npc-pet-form-title]');
      this.state = this.load();
      this.restore();
      this.form.addEventListener('submit', () => this.save());
      this.querySelector('[data-npc-pet-add]')?.addEventListener('click', () => this.startNew());
      this.deleteButton?.addEventListener('click', () => this.remove());
      this.list?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-npc-pet-select]');
        if (button) this.select(button.dataset.npcPetSelect);
      });
    }

    fields() {
      return {
        name: this.querySelector('[data-npc-pet-name]'),
        birthday: this.querySelector('[data-npc-pet-birthday]'),
        weight: this.querySelector('[data-npc-pet-weight]'),
        note: this.querySelector('[data-npc-pet-note]')
      };
    }

    load() {
      let stored = null;
      try {
        stored = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
      } catch (_) {}
      if (stored?.profiles && Array.isArray(stored.profiles)) {
        return {
          version: 2,
          activeId: stored.activeId || stored.profiles[0]?.id || '',
          profiles: stored.profiles.slice(0, 6)
        };
      }
      if (stored?.name) {
        const migrated = { ...stored, id: stored.id || `pet-${Date.now()}` };
        return { version: 2, activeId: migrated.id, profiles: [migrated] };
      }
      const legacyKey = this.storageKey.replace('npc:pet-profiles:', 'npc:pet-profile:');
      try {
        const legacy = JSON.parse(localStorage.getItem(legacyKey) || 'null');
        if (legacy?.name) {
          const migrated = { ...legacy, id: `pet-${Date.now()}` };
          const state = { version: 2, activeId: migrated.id, profiles: [migrated] };
          localStorage.setItem(this.storageKey, JSON.stringify(state));
          return state;
        }
      } catch (_) {}
      return { version: 2, activeId: '', profiles: [] };
    }

    restore() {
      this.renderList();
      if (this.state.activeId) this.select(this.state.activeId);
      else this.startNew();
    }

    renderList() {
      if (!this.list) return;
      this.list.replaceChildren();
      this.state.profiles.forEach((profile) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.npcPetSelect = profile.id;
        button.classList.toggle('is-active', profile.id === this.state.activeId);
        const avatar = document.createElement('span');
        avatar.textContent = profile.animal === 'cat' ? 'K' : 'H';
        const copy = document.createElement('div');
        const name = document.createElement('strong');
        name.textContent = profile.name || 'Namnlös profil';
        const details = document.createElement('small');
        details.textContent = `${profile.animal === 'cat' ? 'Katt' : 'Hund'}${profile.weight ? ` · ${profile.weight} kg` : ''}`;
        copy.append(name, details);
        button.append(avatar, copy);
        this.list.appendChild(button);
      });
      if (this.count) this.count.textContent = `${this.state.profiles.length} ${this.state.profiles.length === 1 ? 'djur' : 'djur'}`;
    }

    select(id) {
      const profile = this.state.profiles.find((item) => item.id === id);
      if (!profile) return;
      this.state.activeId = id;
      if (this.idInput) this.idInput.value = profile.id;
      const fields = this.fields();
      if (fields.name) fields.name.value = profile.name || '';
      if (fields.birthday) fields.birthday.value = profile.birthday || '';
      if (fields.weight) fields.weight.value = profile.weight || '';
      if (fields.note) fields.note.value = profile.note || '';
      const animal = this.querySelector(`input[name="pet_animal"][value="${CSS.escape(profile.animal || '')}"]`);
      if (animal) animal.checked = true;
      if (this.mode) this.mode.textContent = 'Redigera profil';
      if (this.formTitle) this.formTitle.textContent = profile.name || 'Djurprofil';
      if (this.deleteButton) this.deleteButton.hidden = false;
      if (this.status) this.status.textContent = '';
      this.renderList();
    }

    startNew() {
      if (this.state.profiles.length >= 6) {
        if (this.status) this.status.textContent = 'Du kan spara högst sex djur på den här enheten.';
        return;
      }
      this.state.activeId = '';
      this.form.reset();
      if (this.idInput) this.idInput.value = '';
      if (this.mode) this.mode.textContent = 'Nytt djur';
      if (this.formTitle) this.formTitle.textContent = 'Lägg till profil';
      if (this.deleteButton) this.deleteButton.hidden = true;
      if (this.status) this.status.textContent = '';
      this.renderList();
      this.querySelector('[data-npc-pet-name]')?.focus({ preventScroll: true });
    }

    save() {
      const data = new FormData(this.form);
      const profile = {
        id: String(data.get('pet_id') || `pet-${Date.now()}`),
        name: String(data.get('pet_name') || '').trim(),
        animal: String(data.get('pet_animal') || ''),
        birthday: String(data.get('pet_birthday') || ''),
        weight: String(data.get('pet_weight') || ''),
        note: String(data.get('pet_note') || '').trim(),
        updatedAt: new Date().toISOString()
      };
      if (!profile.name || !profile.animal) return;
      const index = this.state.profiles.findIndex((item) => item.id === profile.id);
      if (index >= 0) this.state.profiles[index] = profile;
      else if (this.state.profiles.length < 6) this.state.profiles.push(profile);
      this.state.activeId = profile.id;
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.state));
        localStorage.setItem('npc:active-pet-profile', JSON.stringify(profile));
      } catch (_) {}

      const baseTags = (this.dataset.existingTags || '').split('|').map((tag) => tag.trim()).filter(Boolean);
      const profileTags = ['nordic-pet-club'];
      this.state.profiles.forEach((item) => {
        if (item.animal) profileTags.push(`pet-type-${item.animal}`);
        if (item.birthday) profileTags.push(`pet-birth-month-${item.birthday.slice(5, 7)}`);
        if (item.weight) {
          const weight = Number(item.weight);
          profileTags.push(weight < 10 ? 'pet-size-small' : weight <= 25 ? 'pet-size-medium' : 'pet-size-large');
        }
      });
      if (this.tagsInput) this.tagsInput.value = [...new Set([...baseTags, ...profileTags])].join(',');
      if (this.idInput) this.idInput.value = profile.id;
      if (this.status) this.status.textContent = `${profile.name} är sparad och redo i Nordic Match.`;
      this.renderList();
      window.NPCTrack?.('npc_pet_profile_saved', {
        animal: profile.animal,
        profile_count: this.state.profiles.length
      });
      window.dispatchEvent(new CustomEvent('npc:pet-profile-change', { detail: { profileCount: this.state.profiles.length } }));
    }

    remove() {
      const id = this.idInput?.value || this.state.activeId;
      const profile = this.state.profiles.find((item) => item.id === id);
      if (!profile || !window.confirm(`Ta bort profilen för ${profile.name}?`)) return;
      this.state.profiles = this.state.profiles.filter((item) => item.id !== id);
      this.state.activeId = this.state.profiles[0]?.id || '';
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch (_) {}
      this.restore();
      if (this.status) this.status.textContent = `${profile.name} är borttagen.`;
      window.NPCTrack?.('npc_pet_profile_removed', { profile_count: this.state.profiles.length });
    }
  }

  function openAlert(button) {
    const dialog = document.querySelector('[data-npc-alert-dialog]');
    if (!dialog) return;
    const title = button.dataset.productTitle || 'produkten';
    const handle = button.dataset.productHandle || '';
    const copy = dialog.querySelector('[data-npc-alert-copy]');
    const tags = dialog.querySelector('[data-npc-alert-tags]');
    const note = dialog.querySelector('[data-npc-alert-note]');
    if (copy) copy.textContent = `Få ett mejl när ${title} går att köpa. Ingen spam — bara relevant information om ditt val.`;
    if (tags) tags.value = `newsletter,product-alert,notify-${handle}`.replace(/,+$/, '');
    if (note) note.value = `Produktbevakning: ${title} (${handle})`;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }

  document.addEventListener('click', (event) => {
    const notify = event.target.closest('[data-npc-notify-product]');
    if (notify) {
      event.preventDefault();
      openAlert(notify);
      return;
    }
    const close = event.target.closest('[data-npc-alert-close]');
    if (close) close.closest('dialog')?.close();

    const compare = event.target.closest('[data-npc-compare-product]');
    if (compare && !compare.closest('npc-compare')) {
      event.preventDefault();
      const handle = compare.dataset.npcCompareProduct;
      const tray = document.querySelector('nph-compare-tray');
      if (tray && typeof tray.toggle === 'function') {
        tray.toggle(handle, compare.dataset.nphProductTitle);
        return;
      }
      let handles = [];
      try {
        handles = JSON.parse(localStorage.getItem('npc:compare-products') || '[]');
      } catch (_) {}
      handles = [...new Set([...(Array.isArray(handles) ? handles : []), handle])].filter(Boolean).slice(-3);
      try {
        localStorage.setItem('npc:compare-products', JSON.stringify(handles));
      } catch (_) {}
      window.location.href = `/collections/all?view=jamfor&products=${encodeURIComponent(handles.join(','))}`;
    }
  });

  document.addEventListener('click', (event) => {
    const dialog = event.target.closest('[data-npc-alert-dialog]');
    if (dialog && event.target === dialog) dialog.close();
  });

  if (window.location.hash === '#npc-product-alert-form' && document.querySelector('[data-npc-alert-success]')) {
    const dialog = document.querySelector('[data-npc-alert-dialog]');
    if (dialog?.showModal) dialog.showModal();
  }

  if (!customElements.get('npc-match')) customElements.define('npc-match', NpcMatch);
  if (!customElements.get('npc-compare')) customElements.define('npc-compare', NpcCompare);
  if (!customElements.get('npc-daily-cost')) customElements.define('npc-daily-cost', NpcDailyCost);
  if (!customElements.get('npc-pet-profile')) customElements.define('npc-pet-profile', NpcPetProfile);
})();
